// Small helpers for surfacing structured API errors from the backend.
//
// The backend (api-server) emits errors in this shape on failure:
//   {
//     status: false,
//     error: { code: "SEARCH_UNAVAILABLE" | "SEARCH_FAILED" | ..., message: "..." }
//   }
// or for legacy endpoints just:
//   { status: false, message: "..." }
//
// RTK Query wraps that in an error object with .status (HTTP status) and
// .data (the response body). These helpers normalise the shape so
// components don't have to dig through both.

/**
 * Normalises an RTK Query error (or a fetch Response) into a flat
 * { httpStatus, code, message } object the UI can act on.
 *
 * Always returns an object — never null — so components can safely
 * destructure even when there's no error in flight.
 */
export function parseApiError(err) {
  if (!err) return { httpStatus: null, code: null, message: null };

  // RTK Query FetchBaseQueryError
  const httpStatus =
    typeof err.status === 'number'
      ? err.status
      : typeof err.originalStatus === 'number'
        ? err.originalStatus
        : null;

  const body = err.data || err.body || null;
  const code = body?.error?.code || body?.code || null;
  const message =
    body?.error?.message ||
    body?.message ||
    err?.message ||
    null;

  return { httpStatus, code, message };
}

/**
 * Map a parsed error to a user-friendly display string. Specific to
 * the codes the backend currently emits — extend as new codes land.
 *
 * @param err RTK Query error or null
 * @param fallback string shown when nothing more specific matches
 */
export function getApiErrorMessage(err, fallback = 'Something went wrong. Please try again.') {
  const { httpStatus, code, message } = parseApiError(err);
  if (!httpStatus) return null;

  // Our breaker-tripped responses
  if (httpStatus === 503) {
    if (code === 'SEARCH_UNAVAILABLE') {
      return 'Search is temporarily unavailable. Please retry in a few seconds.';
    }
    if (code === 'SEARCH_FAILED') {
      return 'Search is having trouble right now. Please retry shortly.';
    }
    return message || 'Service is temporarily unavailable. Please retry shortly.';
  }

  // Standard buckets
  if (httpStatus === 401) return 'Your session has expired. Please log in again.';
  if (httpStatus === 403) return "You don't have permission to perform this action.";
  if (httpStatus === 404) return message || 'Not found.';
  if (httpStatus === 429) return 'Too many requests — please slow down and try again shortly.';
  if (httpStatus >= 500) return fallback;
  // 4xx — show backend message if available, otherwise fallback
  return message || fallback;
}

/** True if the error is a "retry-soon" condition the FE should show retryable UI for. */
export function isRetryableApiError(err) {
  const { httpStatus, code } = parseApiError(err);
  if (httpStatus === 503) return true;
  if (httpStatus === 429) return true;
  if (code === 'SEARCH_UNAVAILABLE' || code === 'SEARCH_FAILED') return true;
  return false;
}
